export const surgeryModule = {
  id: "surgery",
  title: "Thyroid Surgery: Part 1",
  sections: [
    {
      type: "text",
      title: "Indications for Thyroid Surgery",
      content: `
        <p>Surgery is one of three definitive treatment options for thyroid disease (alongside radioactive iodine and long-term medical therapy). The indication determines the extent of resection.</p>

        <div class="highlight-box">
          <p><strong>Malignancy or high suspicion</strong></p>
          <ul>
            <li>Confirmed thyroid carcinoma on FNAC (Bethesda V or VI)</li>
            <li>Bethesda IV (follicular neoplasm) — <span class="purple-term">diagnostic hemithyroidectomy</span> (cannot distinguish adenoma from carcinoma on cytology alone; histological capsular/vascular invasion required)</li>
            <li>Suspicious USS features with non-diagnostic cytology</li>
            <li>Medullary thyroid carcinoma (MTC) — surgery is the <em>only</em> curative treatment; RAI is ineffective</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Benign thyroid disease</strong></p>
          <ul>
            <li><strong>Graves' disease</strong> — preferred when:
              <ul>
                <li>Very large goitre (RAI less effective; takes months to shrink)</li>
                <li>Moderate-to-severe active Graves' orbitopathy (RAI may worsen eye disease)</li>
                <li>Coexisting suspicious nodule</li>
                <li>Pregnancy (2nd trimester if surgery essential)</li>
                <li>Patient preference or failure of medical therapy</li>
              </ul>
            </li>
            <li><strong>Multinodular goitre (MNG)</strong> — compressive symptoms: dysphagia, stridor, vocal cord compression, SVC obstruction; substernal extension</li>
            <li><strong>Toxic adenoma or toxic MNG</strong> — when RAI is contraindicated or patient declines</li>
            <li>Cosmesis (large visible goitre)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Airway/compressive indications — urgent considerations</strong></p>
          <ul>
            <li>Stridor suggests significant tracheal compression — CT neck/chest to assess extent; anaesthetic assessment essential (potential difficult airway)</li>
            <li><span class="purple-term">Tracheomalacia</span>: long-standing goitre may soften tracheal cartilage rings; rare but the trachea may collapse immediately after goitre removal — anaesthetist must be prepared for this possibility</li>
            <li>Pemberton's sign: elevation of arms above head → facial congestion/cyanosis/SVC obstruction from thoracic inlet narrowing</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 45-year-old patient has a 4 cm thyroid nodule with Bethesda IV (follicular neoplasm) on FNAC. What is the appropriate surgical management?",
      options: [
        "Immediate total thyroidectomy — follicular neoplasms are almost always malignant",
        "Diagnostic hemithyroidectomy — FNAC cannot distinguish follicular adenoma from carcinoma; histology is required",
        "Reassure and repeat FNAC in 6 months — most follicular lesions are benign",
        "Radioactive iodine ablation — avoids the need for surgery",
      ],
      correct: 1,
      explanation:
        "Bethesda IV (follicular neoplasm/suspicious for follicular neoplasm) carries a malignancy risk of ~25–30%. Crucially, FNAC cannot distinguish follicular adenoma from follicular carcinoma — the defining criterion is histological evidence of capsular or vascular invasion, which requires the entire excised specimen. Hemithyroidectomy (lobectomy) is therefore both diagnostic and therapeutic. If the permanent histology confirms carcinoma, completion thyroidectomy may follow. Total thyroidectomy upfront is not necessary as the majority are benign.",
    },
    {
      type: "question",
      text: "A patient with Graves' disease and moderate-severe active Graves' orbitopathy wishes definitive treatment. Why is surgery preferred over radioactive iodine in this case?",
      options: [
        "RAI is less effective than surgery in Graves' disease",
        "RAI worsens Graves' orbitopathy — the release of thyroid antigens post-RAI amplifies the orbital autoimmune response",
        "RAI cannot be used in patients who have already received antithyroid drugs",
        "Surgery achieves a faster cure of hyperthyroidism than RAI",
      ],
      correct: 1,
      explanation:
        "Radioactive iodine causes destruction of thyroid follicular cells with release of thyroid antigens (including TSH receptor antigen) into the circulation. This can amplify the orbital autoimmune response and worsen Graves' orbitopathy — particularly concerning in moderate-to-severe active disease. Thyroidectomy removes the antigen source more acutely, with a smaller antigenic stimulus. If RAI is used in patients with mild orbitopathy, prophylactic oral prednisolone is given to mitigate this risk. Surgery is therefore preferred when orbitopathy is active and significant.",
    },
    {
      type: "text",
      title: "Types of Thyroid Surgery",
      content: `
        <p>The extent of resection is determined by the indication — more extensive surgery offers better oncological clearance but carries greater risk of complications.</p>

        <div class="highlight-box">
          <p><strong>Hemithyroidectomy (lobectomy ± isthmusectomy)</strong></p>
          <ul>
            <li>Removal of one lobe and the isthmus</li>
            <li>Indications: Bethesda IV nodule (diagnostic); low-risk PTC ≤1 cm (microcarcinoma); solitary toxic adenoma; unilateral benign nodule</li>
            <li>Advantage: preserves contralateral lobe function — ~70% remain euthyroid and do NOT need levothyroxine</li>
            <li>Risk: if cancer found → <span class="purple-term">completion thyroidectomy</span> (removal of remaining lobe) may be needed</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Total thyroidectomy</strong></p>
          <ul>
            <li>Removal of the entire thyroid gland</li>
            <li>Indications: thyroid carcinoma (PTC >1 cm, FTC, MTC, anaplastic); bilateral Graves' disease/MNG; large goitre with bilateral disease</li>
            <li>Enables post-operative radioactive iodine ablation (requires no residual normal thyroid tissue) and thyroglobulin monitoring as a tumour marker</li>
            <li>Mandatory consequences: <strong>lifelong levothyroxine replacement</strong>; higher risk of permanent hypoparathyroidism than hemithyroidectomy</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Lymph node dissection</strong></p>
          <ul>
            <li><span class="purple-term">Central neck dissection (level VI)</span> — lymph nodes between the carotid arteries, from hyoid to innominate vein; performed with total thyroidectomy in confirmed or suspected PTC/MTC; prophylactic central dissection debated for PTC</li>
            <li><span class="purple-term">Lateral neck dissection (levels II–V)</span> — performed when lateral node involvement is confirmed (not prophylactically); requires clinical or imaging evidence of nodal disease</li>
            <li>MTC: pre-operative calcitonin >200–500 pg/mL predicts central nodal disease → central dissection planned upfront</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Surgical approaches</strong></p>
          <ul>
            <li>Conventional open cervicotomy — standard; transverse Kocher incision in a skin crease</li>
            <li>Minimally invasive video-assisted thyroidectomy (MIVAT) — selected smaller glands; smaller incision</li>
            <li>Robotic/remote access — axillary, transoral, or retroauricular; avoids neck scar; select centres; longer operative time</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 35-year-old has total thyroidectomy for papillary thyroid carcinoma. Which of the following is a necessary consequence of this procedure?",
      options: [
        "Permanent hypoparathyroidism requiring IV calcium indefinitely",
        "Lifelong levothyroxine replacement therapy",
        "Loss of voice due to bilateral recurrent laryngeal nerve division",
        "Inability to undergo future radioactive iodine — surgery precludes RAI",
      ],
      correct: 1,
      explanation:
        "After total thyroidectomy, all functioning thyroid tissue is removed, so lifelong levothyroxine replacement is mandatory and universal. Permanent hypoparathyroidism occurs in ~1–2% (not all patients); most transient hypocalcaemia resolves within weeks to months as parathyroid glands recover from surgical manipulation. Bilateral RLN injury is a rare complication (~0.5%), not a deliberate outcome. Importantly, total thyroidectomy enables (rather than precludes) RAI ablation — it removes normal thyroid tissue so that RAI can target residual cancer cells specifically.",
    },
    {
      type: "text",
      title: "Pre-operative Preparation",
      content: `
        <p>Careful pre-operative preparation reduces operative risk and prevents potentially dangerous complications such as thyroid storm or intraoperative haemorrhage.</p>

        <div class="highlight-box">
          <p><strong>1. Render the patient euthyroid</strong></p>
          <ul>
            <li>All hyperthyroid patients (Graves', toxic MNG, toxic adenoma) must be <strong>euthyroid</strong> before surgery — prevents thyroid storm from handling/manipulation of the gland</li>
            <li>Achieve with carbimazole/PTU; usually takes 4–8 weeks</li>
            <li>Beta-blockers (propranolol) can be added for rapid symptom control during preparation</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>2. Lugol's iodine (potassium iodide solution)</strong></p>
          <ul>
            <li>Given for <strong>10 days pre-operatively</strong> in Graves' disease surgery</li>
            <li>Large iodine load → Wolff-Chaikoff effect → ↓hormone release + <span class="purple-term">reduces thyroid vascularity</span> and gland firmness</li>
            <li>Effect: firmer, less vascular gland → reduced intraoperative bleeding and easier surgical dissection</li>
            <li>Must be given <strong>after</strong> antithyroid drug is established (≥1 hour gap; do not give iodide before blocking synthesis)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>3. Baseline investigations</strong></p>
          <ul>
            <li><span class="purple-term">Laryngoscopy</span> — mandatory pre-operative vocal cord assessment; identifies any pre-existing RLN palsy (important medico-legally and to establish baseline before contralateral surgery)</li>
            <li><strong>Serum calcium and PTH</strong> — baseline before parathyroid glands are at risk</li>
            <li><strong>TFTs</strong> — confirm euthyroid status on the day of surgery</li>
            <li><strong>Imaging</strong>: USS is routine; CT neck and chest if substernal goitre, large tumour, or tracheal deviation (defines extent of disease and potential airway challenges)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>4. Anaesthetic and airway assessment</strong></p>
          <ul>
            <li>Tracheal deviation or compression → potentially difficult intubation → discuss with anaesthetist; may require awake fibreoptic intubation</li>
            <li>Tracheomalacia risk (long-standing goitre) — anaesthetist should be prepared to keep endotracheal tube in situ and perform tracheal stenting if required</li>
            <li>Redo surgery (completion thyroidectomy or reoperative neck dissection): significantly higher risk of RLN injury due to scar tissue</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with Graves' disease is being prepared for total thyroidectomy. Lugol's iodine is prescribed. When should it be started relative to antithyroid drug therapy?",
      options: [
        "Lugol's should be started 2 weeks before antithyroid drugs to maximise the Wolff-Chaikoff effect",
        "Lugol's should only begin once antithyroid drugs have been established — giving iodide first can transiently worsen hormone production (Jod-Basedow)",
        "Lugol's and antithyroid drugs should be started simultaneously on the same day",
        "Lugol's is not needed if the patient has been on antithyroid drugs for more than 4 weeks",
      ],
      correct: 1,
      explanation:
        "Lugol's iodine must only be introduced once antithyroid drugs (carbimazole/PTU) are already established. If given first, the iodide load provides extra substrate to an uninhibited TPO, potentially triggering a burst of hormone synthesis (Jod-Basedow effect). Once TPO is blocked, Lugol's solution produces the desired Wolff-Chaikoff effect: inhibiting hormone release and reducing gland vascularity, making surgery safer and easier. The standard protocol is Lugol's 10 days pre-operatively, after confirming the patient is on established antithyroid therapy.",
    },
    {
      type: "text",
      title: "The Recurrent Laryngeal Nerve & External Branch of SLN",
      content: `
        <p>The recurrent laryngeal nerve (RLN) and the external branch of the superior laryngeal nerve (EBSLN) are the two nerves at greatest risk during thyroid surgery. Understanding their anatomy is essential.</p>

        <div class="highlight-box">
          <p><strong>Recurrent laryngeal nerve (RLN)</strong></p>
          <ul>
            <li>Supplies all intrinsic laryngeal muscles <strong>except cricothyroid</strong> (which is supplied by the EBSLN)</li>
            <li>Function: vocal cord adduction/abduction (voice, airway protection, cough)</li>
            <li><strong>Anatomical course differences:</strong>
              <ul>
                <li><span class="purple-term">Right RLN</span> — loops under the <strong>right subclavian artery</strong>; shorter retroesophageal course; more oblique approach to the larynx</li>
                <li><span class="purple-term">Left RLN</span> — loops under the <strong>aortic arch</strong> (at the ligamentum arteriosum); longer course up the tracheoesophageal groove; more predictable location</li>
              </ul>
            </li>
            <li><strong>Non-recurrent RLN</strong>: occurs on the right in ~0.5–1% when there is an aberrant right subclavian artery (<em>arteria lusoria</em>) — the nerve does not loop under the subclavian but travels directly to the larynx from the vagus; at high risk of unexpected injury if surgeon is not aware</li>
            <li>The RLN is most at risk near the <strong>Berry's ligament</strong> (suspensory ligament of the thyroid) and the inferior thyroid artery — the nerve may branch here; identified with intraoperative neuromonitoring</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Consequences of RLN injury</strong></p>
          <ul>
            <li><strong>Unilateral injury</strong>: ipsilateral vocal cord palsy — hoarse, breathy voice; aspiration risk; often improves (temporary neuropraxia resolves weeks–months); if permanent, medialisation procedures (injection laryngoplasty) can improve voice</li>
            <li><strong>Bilateral injury</strong>: both cords adduct to midline → <span class="purple-term">respiratory distress, stridor, inability to cough</span> → requires immediate re-intubation and tracheostomy; rare but life-threatening</li>
            <li>Incidence: ~5–10% temporary; ~1% permanent with experienced surgeon</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>External branch of the superior laryngeal nerve (EBSLN)</strong></p>
          <ul>
            <li>Also called the "high note nerve" or "surgeon's nerve" (often unintentionally cut)</li>
            <li>Supplies the <strong>cricothyroid muscle</strong> — tenses the vocal cord → controls pitch and projection</li>
            <li>At risk during ligation of the <strong>superior thyroid vessels</strong> (superoposterior dissection)</li>
            <li>Injury → loss of high-pitched phonation; singers most affected; fatigue on prolonged speech; not hoarseness per se</li>
            <li>Identified in only ~50% of operations (often not visible); should ligate superior pole vessels <em>close to the thyroid capsule</em> to protect the nerve</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Intraoperative neuromonitoring (IONM)</strong></p>
          <ul>
            <li>Electromyography electrodes on endotracheal tube detect RLN signal in vocalis/thyroarytenoid muscles</li>
            <li>Continuous or intermittent stimulation of the RLN confirms its function in real time</li>
            <li>Loss of signal → surgeon pauses dissection; identifies stretch or compression before irreversible damage</li>
            <li>Particularly important in redo surgery, large goitres, and cancer with nerve involvement</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient wakes from total thyroidectomy with a hoarse voice. Flexible laryngoscopy shows a right vocal cord in the paramedian position with impaired movement. The left cord moves normally. What is the diagnosis and expected prognosis?",
      options: [
        "Bilateral RLN injury — emergency tracheostomy is required",
        "Right RLN neuropraxia (temporary injury) — most cases of unilateral palsy recover within weeks to months",
        "Cricothyroid muscle damage — the EBSLN was injured bilaterally",
        "Tracheomalacia causing stridor and voice change",
      ],
      correct: 1,
      explanation:
        "Unilateral RLN palsy (right in this case) causes ipsilateral vocal cord immobility in the paramedian position — producing a breathy, hoarse voice and some aspiration risk. The contralateral cord compensates over time by crossing the midline. Most cases are neuropraxia (bruising, stretching, or temporary devascularisation) and recover fully within 3–6 months. Only ~1% become permanent. Bilateral RLN injury would present with stridor and respiratory distress requiring tracheostomy. EBSLN injury affects pitch, not cord movement.",
    },
    {
      type: "question",
      text: "During a right hemithyroidectomy, the surgeon is surprised to find the RLN approaching the larynx directly without the usual loop under the subclavian artery. What anatomical variant is present and what is its significance?",
      options: [
        "The RLN looped under the aortic arch instead — this is the normal right-sided anatomy",
        "A non-recurrent laryngeal nerve — associated with an aberrant right subclavian artery (arteria lusoria); the nerve is at very high risk if the surgeon is unaware",
        "The left and right RLNs have swapped sides — a known congenital variant",
        "The nerve is bifurcated — an anatomical variant with no clinical significance",
      ],
      correct: 1,
      explanation:
        "A non-recurrent laryngeal nerve (NRLN) occurs almost exclusively on the right (~0.5–1%) and is invariably associated with an aberrant right subclavian artery (arteria lusoria) — the subclavian arises from the descending aorta directly, passing posterior to the oesophagus. Without the normal subclavian loop, the nerve takes a short direct course from the vagus to the larynx, entering the operative field unexpectedly. If the surgeon expects the normal anatomy and does not identify it, the NRLN is very easily divided. Intraoperative neuromonitoring is especially valuable in detecting this variant.",
    },
  ],
};
